import type { GQL } from '@aline/graphql';
import type { BaseElement } from 'slate';

import type { UUID } from '../../../types/type-helpers';
import type { AlineContentBlock, AlineText, LocationIdProps, SuggestedEdit } from '../../internal';

export type DynamicContentElement = DynamicBlockElement | DynamicInlineElement;
export type DynamicContentType = DynamicBlockType | DynamicInlineType;
export type DynamicContentProps = Omit<DynamicContentElement, 'children'>;
export type DynamicBlockProps = Omit<DynamicBlockElement, 'children'>;

export const dynamicBlockType = 'dynamic-block';
export type DynamicBlockType = typeof dynamicBlockType;

interface SharedDynamicContentElementProps {
  /** Which option field driving this dynamic content. */
  fieldId: UUID;
  type: DynamicContentType;
  /** The initial value for `triggerIndex` when loading a document. */
  initialTriggerIndex?: number | 'not-set';
  suggestedEdits?: SuggestedEdit[] | null;
  /** Points to which trigger is resolved, if any. */
  triggerIndex?: number | 'not-set';
  /** List of triggers that, if met, will cause this element to render the corresponding trigger's content. */
  triggers?: Trigger[];
  /** Is the dynamic content's option field a dropdown or radio button? */
  variant?: GQL.FieldVariant.Dropdown | GQL.FieldVariant.Radiobutton;
}

export interface DynamicBlockElement
  extends BaseElement, SharedDynamicContentElementProps, LocationIdProps {
  children: AlineContentBlock[];
  type: DynamicBlockType;
}

export const dynamicInlineType = 'dynamic-inline';
export type DynamicInlineType = typeof dynamicInlineType;

export interface DynamicInlineElement extends BaseElement, SharedDynamicContentElementProps {
  children: AlineText[];
  type: DynamicInlineType;
}

/** How do join the provided conditionals. Default: 'and'. */
export type LinkOperator = 'and';

export interface Trigger {
  conditions: TriggerCondition[];
  /** Content that should appear if this trigger is matched. */
  contentJSON: DynamicBlockElement['children'] | DynamicInlineElement['children'];
  linkOperator: LinkOperator;
}

/** Conditions under which the trigger should apply. */
export type TriggerCondition = TriggerConditionOnOptionChoice;

export interface TriggerConditionOnOptionChoice {
  answerOptionChoiceId: UUID;
  conditionType: 'option-choice';
  /** nodeId of the variable that drives this choice */
  fieldId: UUID;
}
